import { useEffect, useMemo } from "react";
import { useLocation, useRoute } from "wouter";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import useMusioApi from "@/contexts/client/musio-client-context";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { Filter, FilterHeader, FilterOption, FilterSection } from "@/components/ui/filter";
import { FILTER_CATEGORIES, Collection, useProducts } from "@/features/marketplace";
import { multiSelect, norm, useFilteredList, useFilterState } from "@/utils/filter";
import { Helmet } from "@dr.pogodin/react-helmet";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { Sidebar, SidebarContent, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";

type FilterState = {
  categories: string[];
  developer: string;
}

const CARD_IMAGE_KEY = "card_img";

export default function DeveloperPage() {
  const [, params] = useRoute('/developer/:developerSlug');
  const developerSlug = params?.developerSlug;
  const { musioApi } = useMusioApi();
  const [_, navigate] = useLocation();
  const { items, loading: productsLoading } = useProducts();
  const isMobile = useIsMobile(1026);

  const { state: filters, set } = useFilterState<FilterState>({
    categories: [],
    developer: "",
  });

  const predicates = useMemo(() => [
    multiSelect(
      (item: any) => item.tags?.map((t: string) => norm(t)) ?? [],
      (s: FilterState) => s.categories.map(norm),
      true,
    ),

    (item: any, s: FilterState) =>
      !s.developer || norm(item.developerName) === norm(s.developer),
  ], []);

  const filteredItems = useFilteredList(items, filters, predicates);

  const { data: developer, isPending, isError, error } = musioApi.useQuery(
    "get",
    "/catalog/developers/{developerIdOrSlug}",
    { params: { path: { developerIdOrSlug: developerSlug! } } }
  );

  useEffect(() => {
    if (!developer?.developerName) return;
    if (norm(filters.developer) !== norm(developer.developerName)) {
      set("developer")(developer.developerName);
    }
  }, [developer?.developerName, filters.developer, set]);

  useEffect(() => {
    if (isError) {
      console.error("Error getting developer data", error);
      navigate("~/404");
    }
  }, [isError, error, navigate]);

  if (isPending || productsLoading) {

    return (
      <>
        <Helmet>
          <title>{developer?.developerName} | Musio Marketplace</title>
          <meta name="description" content={developer?.developerDescription} />
          <meta name="keywords" content={items.map((i) => i.productName).join(",")} />
        </Helmet>

        <div className="h-full flex items-center justify-center musio-glass">
          <LoadingIndicator />
        </div>
      </>
    );
  }

  return (
    <>
      {developer &&
        <Helmet>
          <title>{developer?.developerName} | Musio Marketplace</title>
          <meta name="description" content={developer?.developerDescription} />
          <meta name="keywords" content={items.map((i) => i.productName).join(",")} />
        </Helmet>
      }
      <SidebarProvider
        className="
          h-full gap-2
          grid grid-cols-1 grid-rows-[auto_1fr]
          lg:grid-cols-5 lg:grid-rows-1 
        "
        defaultOpen={false}
      >
        {isMobile ?
          <Sidebar>
            <SidebarContent className="musio-glass">
              <Filter
                multiSelect
                className="p-3"
                onFilterChange={set("categories")}
                activeOptions={filters.categories}
              >
                <FilterHeader className="px-3 pb-4">Categories</FilterHeader>
                <FilterSection>
                  {FILTER_CATEGORIES.map((item, index) => (
                    <FilterOption key={index} className="p-2">{item}</FilterOption>
                  ))}
                </ FilterSection>
              </Filter>

            </SidebarContent>
          </Sidebar> :
          <div className="musio-glass hidden lg:block lg:col-span-1 min-h-0 overflow-auto">
            <Filter
              multiSelect
              className="p-3"
              onFilterChange={set("categories")}
              activeOptions={filters.categories}
            >
              <FilterHeader className="px-3 pb-4">Categories</FilterHeader>
              <FilterSection>
                {FILTER_CATEGORIES.map((item, index) => (
                  <FilterOption key={index} className="p-2">{item}</FilterOption>
                ))}
              </ FilterSection>
            </Filter>
          </div>
        }

        <SidebarTrigger className="lg:hidden" />
        <ScrollArea className="px-10 py-4 w-full h-full musio-glass space-y-2 flex flex-col min-h-0 overflow-auto col-span-4">
          <div className="mb-4">
            <Button
              className="bg-musio-green text-musio-mid-gray text-xs size-fit over:bg-musio-green active:bg-musio-green"
              onClick={() => navigate("/")}
            >
              <ChevronLeft />
              Back
            </Button>
          </div>


          <div className="flex flex-col items-center lg:items-start lg:flex-row flex-1 space-x-5">
            <div className="h-[200px] w-[200px] shrink-0">
              <img
                className="w-full h-full object-cover"
                src={developer?.assets[CARD_IMAGE_KEY].url}
              />
            </div>
            <div className="flex flex-col grow justify-start space-y-3">
              <div className="flex flex-row justify-between items-baseline space-y-0.5">
                <h2 className="text-musio-light-lilac text-xl font-semibold">{developer?.developerName}</h2>
                <a href={developer?.developerWebsite} target="_blank" className="font-normal text-[10px]" style={{ color: "rgba(170, 144, 197, 0.5)" }}>{developer?.developerWebsite}</a>
              </div>
              <p className="font-normal text-musio-light-lilac text-sm">
                {developer?.developerDescription}
              </p>
            </div>
          </div>


          <div className="flex flex-col py-4 flex-grow min-h-0"> {/*overflow-hidden*/}
            {/* <ScrollArea className="flex-1 h-full w-full border-t-2 border-solid border-musio-mid-gray"> */}
            <div className="flex w-full flex-col space-y-4">
              {filteredItems.map((item) => (
                <Collection
                  key={item.id}
                  item={item}
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.25)" }}
                  variant="marketplace"
                  onClick={() => navigate(`~/marketplace/collection/${item.collectionSlug}`)}
                />
              ))}
            </div>
            {/* <ScrollBar orientation="vertical" />
            </ScrollArea> */}
          </div>
          <ScrollBar orientation="vertical" />
        </ScrollArea>

      </SidebarProvider >
    </>
  );
}
